import { forwardRef } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

const DEFAULT_ICON = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle
};

/**
 * An inline status banner (spec §7.2). Tone is never colour-only — every tone
 * pairs with a distinct glyph by default (override with `icon`, or pass
 * `icon={null}` to suppress it entirely).
 *
 * @param {object} props
 * @param {'info'|'success'|'warning'|'danger'} [props.tone='info']
 * @param {import('react').ReactNode} [props.title]
 * @param {import('react').ReactNode} props.children - the body copy.
 * @param {import('react').ElementType|null} [props.icon] - overrides the
 *   tone's default lucide-react icon; `null` renders no icon.
 * @param {string} [props.className]
 */
export const Alert = forwardRef(function Alert({ tone = 'info', title, children, icon, className = '', ...rest }, ref) {
  const Icon = icon === null ? null : icon || DEFAULT_ICON[tone] || Info;

  return (
    <div
      ref={ref}
      className={`etch-alert ${className}`.trim()}
      data-tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
      {...rest}
    >
      {Icon && (
        <span className="etch-alert__icon" aria-hidden="true">
          <Icon size={18} />
        </span>
      )}
      <div className="etch-alert__body">
        {title && <div className="etch-alert__title">{title}</div>}
        <div>{children}</div>
      </div>
    </div>
  );
});

export default Alert;
